import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  punchApi, usersApi, type PunchItem, type PunchPin, type PunchListResponse, type PunchPhotoInput, type PunchPriority, type PunchAssigneeInput,
} from '@/lib/apiClient';
import { GraphConsentError } from '@/lib/graphUpload';
import { isDefiniteRejection, isConflictError } from '@/lib/httpErrors';
import { useGraphToken } from '@/lib/graphHooks';
import { randomId } from '@/lib/randomId';
import { pickableTrades, defaultFieldLeader } from './punchModel';
import { uploadPunchPhoto, SIGN_IN_FAILED } from './punchPhotos';

/**
 * The add-an-item form's state and its save, shared by the desktop modal and
 * the phone's step-by-step add (#518), so both upload and save the same way.
 *
 * Retry-safe the way site photos are (#560): the photo's file name and the
 * item's clientRequestId are fixed on the first attempt and reused until it
 * succeeds, so a lost reply can't leave a second copy in the Job Folder or a
 * second item on the list.
 */
export function usePunchItemForm({ projectId, data, jobNumber, jobFolderUrl, initialRoom = '', initialPin = null, onAdded }: {
  projectId: number;
  data: PunchListResponse;
  jobNumber: string;
  jobFolderUrl: string | null;
  initialRoom?: string;
  /** Dropped on a drawing (Drop Pin to Add Item): this item goes there. */
  initialPin?: PunchPin | null;
  onAdded: () => void;
}) {
  const graph = useGraphToken();
  const subsQ = useQuery({ queryKey: ['punch', projectId, 'subs'], queryFn: () => punchApi.subs(projectId), staleTime: 60_000 });
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });

  const [photo, setPhoto] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const trades = pickableTrades(data.trades);
  const [picked, setTrade] = useState(trades[0]?.key ?? '');
  // A new item only takes a trade the job offers NOW: if the pick is hidden or
  // removed by someone else while this is open, fall back to the first one
  // rather than keep it and have the item refused after its photo uploaded
  // (Codex on #566). Editing an item keeps its own past trade; adding doesn't.
  const trade = trades.some((t) => t.key === picked) ? picked : (trades[0]?.key ?? '');
  const [assigneePick, setAssignee] = useState<string>('default');
  const [room, setRoom] = useState(initialRoom);
  const [notes, setNotes] = useState('');
  // null until someone picks: the server then applies the same default.
  const [leaderPick, setLeaderPick] = useState<string | null>(null);
  const [due, setDue] = useState('');
  const [priority, setPriority] = useState<PunchPriority | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The drawing pin, for this one item: reset() drops it, so Save & Add
  // Another doesn't put the next item on the same spot.
  const [pin, setPin] = useState<PunchPin | null>(initialPin);

  // Fixed per item until it saves: the photo's tag (so a retry reuses its file
  // name), what already uploaded, and the item's request id.
  // WeakMap, not Map: keyed by the File, so a photo let go of is freed rather
  // than held for the whole add session — the phone keeps this form open
  // across Save & Next (Codex on #593).
  const tags = useRef(new WeakMap<File, string>());
  const uploaded = useRef<{ file: File; photo: PunchPhotoInput } | null>(null);
  const requestId = useRef<string>(randomId());

  const mappedSub = data.list?.tradeSubs.find((t) => t.trade === trade)?.subName ?? null;
  const subNames = useMemo(() => (subsQ.data?.subs ?? []).map((s) => s.name), [subsQ.data]);
  const leaders = subsQ.data?.fieldLeaders ?? [];
  // Picks are kept only while the job still offers them. A field leader taken
  // off the job, or a hand-added name removed, while this is open falls back
  // (to the default leader, or "trade default") instead of being sent after
  // the photo uploaded and refused (Codex on #566) — like the trade above.
  const pickedLeader = leaderPick && leaders.some((l) => l.id.toLowerCase() === leaderPick.toLowerCase()) ? leaderPick : null;
  const leader = pickedLeader ?? defaultFieldLeader(leaders, me?.id) ?? '';
  const assignee = assigneePick.startsWith('sub:') && subsQ.isSuccess
    && !subNames.some((n) => n.toLowerCase() === assigneePick.slice(4).toLowerCase())
    ? 'default'
    : assigneePick;

  /** Ready for the next item: a fresh photo, description and notes; room and trade kept if asked. */
  const reset = (keepPlace: boolean) => {
    setPin(null);
    uploaded.current = null;
    requestId.current = randomId();
    setPhoto(null);
    setDescription('');
    setNotes('');
    if (!keepPlace) setRoom('');
  };

  /** Save the item. Returns the item as saved (the phone puts a clip on it next), or null when it wasn't saved (the error says why). */
  const save = async (): Promise<PunchItem | null> => {
    // The field leaders have to be known before anything uploads: with them
    // still loading (or failed) a job with several would look like it has
    // none, and the photo would upload before the API refused the item
    // (Codex on #566).
    if (!subsQ.isSuccess) {
      setError(subsQ.isError ? 'Couldn’t load this job’s subs and field leaders. Close and try again.' : 'Still loading this job’s subs and field leaders…');
      return null;
    }
    if (!photo) { setError('A before photo is required.'); return null; }
    if (!description.trim()) { setError('Say what needs fixing.'); return null; }
    if (!jobFolderUrl) { setError('Link this job’s Job Folder first — photos are saved there.'); return null; }
    if (leaders.length > 1 && !leader) { setError('Pick the field leader in charge of this item.'); return null; }
    setBusy(true);
    setError(null);
    try {
      if (uploaded.current?.file !== photo) {
        // Sign in straight from the tap, before any work (popup blockers). Any
        // failure reads as SIGN_IN_FAILED, not MSAL's raw error. Skipped when
        // the photo is already up and only the save is being retried.
        try {
          await graph.interactive();
        } catch {
          throw new GraphConsentError('SharePoint sign-in failed');
        }
        let tag = tags.current.get(photo);
        if (!tag) { tag = randomId().slice(0, 8); tags.current.set(photo, tag); }
        const res = await uploadPunchPhoto({
          file: photo, kind: 'before', itemNo: null, jobNumber, jobFolderUrl,
          subfolder: data.photoSubfolder, getToken: graph.interactive, tag,
        });
        uploaded.current = { file: photo, photo: res.photo };
      }
      const item = await punchApi.createItem(projectId, {
        clientRequestId: requestId.current,
        description: description.trim(),
        trade,
        ...(assignee === 'default' ? {} : { assignee: toAssignee(assignee) }),
        room: room.trim() || undefined,
        notes: notes.trim() || undefined,
        ...(pickedLeader ? { fieldLeaderId: pickedLeader } : {}),
        dueDate: due || undefined,
        priority: priority || undefined,
        beforePhoto: uploaded.current!.photo,
        ...(pin ? { pin } : {}),
      });
      onAdded();
      return item;
    } catch (e) {
      if (e instanceof GraphConsentError) setError(SIGN_IN_FAILED);
      else {
        // A refusal can't succeed on retry with the same request id, so start
        // a fresh one: a definite 4xx, and a 409 too ("already added and since
        // removed", "added to a different job", a locked job) — a lost reply
        // is never a 409, the server answers it with the item it made.
        // Anything else (network, 5xx) keeps the id so the retry is recognized.
        if (isDefiniteRejection(e) || isConflictError(e)) requestId.current = randomId();
        setError((e as Error)?.message ?? 'The item was not saved. Try again.');
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  return {
    subsQ, trades, subNames, leaders, mappedSub,
    photo, setPhoto, description, setDescription, trade, setTrade, assignee, setAssignee,
    room, setRoom, notes, setNotes, leader, setLeaderPick, due, setDue, priority, setPriority,
    pin, setPin, busy, error, setError, reset, save,
  };
}

export type PunchItemForm = ReturnType<typeof usePunchItemForm>;

function toAssignee(v: string): PunchAssigneeInput | null {
  if (v === 'none') return null;
  if (v === 'ew_crew') return { type: 'ew_crew' };
  return { type: 'sub', name: v.slice(4) };
}
